import React from 'react';
import { Button, ButtonGroup, Form, Modal } from 'react-bootstrap';
import type { useTabDateNav } from '../hooks/useTabDateNav';

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface DateNavBarProps {
  // The hook's return value drives all state + handlers; the bar is
  // purely presentational so callers can place it anywhere (header,
  // toolbar, tab content) without re-wiring state.
  nav: ReturnType<typeof useTabDateNav>;
  // Optional label shown at the start of the toolbar (e.g. "Income
  // for", "Events for"). Defaults to nothing so the bar is generic.
  leftLabel?: React.ReactNode;
  // Slot for controls that should sit INSIDE the same rounded chrome
  // as the date controls, separated by a vertical divider. Matches
  // the prod layout for the Financials tab's Filters toggle.
  inlineExtra?: React.ReactNode;
  // Right-side slot for tab-specific summaries (e.g. bucket count).
  // Sits outside the rounded chrome.
  rightSlot?: React.ReactNode;
}

// DateNavBar — toolbar with mode buttons, prev/next navigation,
// date-picker trigger, and Today shortcut. Wires the modal at the end.
// Stateful logic lives entirely in useTabDateNav.
const DateNavBar: React.FC<DateNavBarProps> = ({ nav, leftLabel, inlineExtra, rightSlot }) => {
  const {
    dateMode, dateDisplay,
    switchMode, handleNavigate, handleToday,
    showDatePicker, setShowDatePicker,
    pickerYear, setPickerYear,
    pickerQuarter, setPickerQuarter,
    pickerMonth, setPickerMonth,
    pickerStart, setPickerStart,
    pickerEnd, setPickerEnd,
    openDatePicker, applyDatePicker,
  } = nav;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
            {leftLabel && (
              <span className="text-primary fw-semibold">{leftLabel}</span>
            )}
            <ButtonGroup size="sm">
              <Button variant={dateMode === 'ytd' ? 'primary' : 'outline-primary'} onClick={() => switchMode('ytd')}>Current YTD</Button>
              <Button variant={dateMode === 'year' ? 'primary' : 'outline-primary'} onClick={() => switchMode('year')}>Year</Button>
              <Button variant={dateMode === 'quarter' ? 'primary' : 'outline-primary'} onClick={() => switchMode('quarter')}>Quarter</Button>
              <Button variant={dateMode === 'month' ? 'primary' : 'outline-primary'} onClick={() => switchMode('month')}>Month</Button>
              <Button variant={dateMode === 'custom' ? 'primary' : 'outline-primary'} onClick={() => switchMode('custom')}>Custom</Button>
              <Button variant={dateMode === 'all' ? 'primary' : 'outline-primary'} onClick={() => switchMode('all')}>All</Button>
            </ButtonGroup>
            <Button variant="outline-primary" size="sm" onClick={() => handleNavigate('prev')} disabled={dateMode === 'ytd' || dateMode === 'all'}>
              <i className="bi bi-chevron-left"></i>
            </Button>
            <Button
              variant={dateMode !== 'ytd' && dateMode !== 'all' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={openDatePicker}
              disabled={dateMode === 'ytd' || dateMode === 'all'}
              style={{ minWidth: '220px' }}
            >
              <i className="bi bi-calendar-month me-2"></i>{dateDisplay}
            </Button>
            <Button variant="outline-primary" size="sm" onClick={() => handleNavigate('next')} disabled={dateMode === 'ytd' || dateMode === 'all'}>
              <i className="bi bi-chevron-right"></i>
            </Button>
            <Button variant="outline-primary" size="sm" onClick={handleToday} disabled={dateMode === 'ytd' || dateMode === 'all'}>Today</Button>
            {inlineExtra && (
              <>
                <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
                {inlineExtra}
              </>
            )}
          </div>
        </div>
        {rightSlot && (
          <div className="d-flex align-items-center gap-3 flex-wrap">{rightSlot}</div>
        )}
      </div>

      <Modal show={showDatePicker} onHide={() => setShowDatePicker(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Select {dateMode === 'year' ? 'Year' : dateMode === 'quarter' ? 'Quarter' : dateMode === 'month' ? 'Month' : 'Date Range'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {dateMode === 'year' && (
            <div className="text-center py-4">
              <div className="d-flex justify-content-center align-items-center">
                <Button variant="outline-secondary" size="lg" onClick={() => setPickerYear(pickerYear - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </Button>
                <div className="mx-3 text-center" style={{ minWidth: '120px' }}>
                  <h2 className="text-primary mb-0">{pickerYear}</h2>
                </div>
                <Button variant="outline-secondary" size="lg" onClick={() => setPickerYear(pickerYear + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </Button>
              </div>
            </div>
          )}
          {dateMode === 'quarter' && (
            <div>
              <div className="d-flex justify-content-center align-items-center mb-4">
                <Button variant="outline-secondary" size="lg" onClick={() => setPickerYear(pickerYear - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </Button>
                <div className="mx-3 text-center" style={{ minWidth: '120px' }}>
                  <h2 className="text-primary mb-0">{pickerYear}</h2>
                </div>
                <Button variant="outline-secondary" size="lg" onClick={() => setPickerYear(pickerYear + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-2 justify-content-center">
                {[1, 2, 3, 4].map(q => (
                  <Button key={q} variant={pickerQuarter === q ? 'primary' : 'outline-primary'} size="lg" onClick={() => setPickerQuarter(q)} style={{ minWidth: '80px' }}>Q{q}</Button>
                ))}
              </div>
            </div>
          )}
          {dateMode === 'month' && (
            <div>
              <div className="d-flex justify-content-center align-items-center mb-4">
                <Button variant="outline-secondary" size="lg" onClick={() => setPickerYear(pickerYear - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </Button>
                <div className="mx-3 text-center" style={{ minWidth: '120px' }}>
                  <h2 className="text-primary mb-0">{pickerYear}</h2>
                </div>
                <Button variant="outline-secondary" size="lg" onClick={() => setPickerYear(pickerYear + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </Button>
              </div>
              <div className="row g-2">
                {monthNames.map((name, i) => (
                  <div key={i} className="col-3">
                    <Button variant={pickerMonth === i + 1 ? 'primary' : 'outline-primary'} size="lg" className="w-100" onClick={() => setPickerMonth(i + 1)} style={{ minHeight: '50px' }}>{name}</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {dateMode === 'custom' && (
            <>
              <div className="mb-3">
                <label htmlFor="picker-start-date" className="form-label">Start Date</label>
                <Form.Control id="picker-start-date" type="date" value={pickerStart} onChange={e => setPickerStart(e.target.value)} />
              </div>
              <div className="mb-3">
                <label htmlFor="picker-end-date" className="form-label">End Date</label>
                <Form.Control id="picker-end-date" type="date" value={pickerEnd} onChange={e => setPickerEnd(e.target.value)} min={pickerStart} />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDatePicker(false)}>Cancel</Button>
          <Button variant="primary" onClick={applyDatePicker} disabled={dateMode === 'custom' && (!pickerStart || !pickerEnd)}>Apply</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DateNavBar;
